import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Home, User, Phone, Calendar, Building2, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusIcon = ({ ok }) => ok
  ? <CheckCircle2 className="w-14 h-14 text-emerald-500" />
  : <XCircle className="w-14 h-14 text-red-500" />;

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <Icon className="w-4 h-4 text-[#D4A745] mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value || '—'}</p>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

const VerifyAttestation = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/attestations/verify/${encodeURIComponent(id)}`);
        if (!cancelled) setState({ loading: false, cert: res.data, error: null });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          cert: null,
          error: e.response?.data?.detail || 'Attestation introuvable',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1B3A4B] to-[#2D5469]">
        <Loader2 className="w-10 h-10 animate-spin text-white" />
      </div>
    );
  }

  const ok = !!state.cert && !state.cert.revoked;
  const cert = state.cert;
  const snap = cert?.snapshot || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B3A4B] to-[#2D5469] py-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4A745]/20 border border-[#D4A745]/40 mb-3">
            <Shield className="w-4 h-4 text-[#D4A745]" />
            <span className="text-[#D4A745] text-xs font-semibold tracking-wide">VERIFICATION OFFICIELLE</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Attestation de domicile</h1>
          <p className="text-white/70 text-sm mt-1">Référence : <span className="font-mono">{id}</span></p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="verify-result-card">
          {/* Status banner */}
          <div className={`p-6 text-center ${ok ? 'bg-emerald-50 border-b-2 border-emerald-200' : 'bg-red-50 border-b-2 border-red-200'}`}>
            <div className="flex justify-center mb-2">
              <StatusIcon ok={ok} />
            </div>
            <h2 className={`text-xl font-bold ${ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="verify-status">
              {!cert ? 'Attestation introuvable'
                : cert.revoked ? 'Attestation révoquée'
                : 'Attestation authentique'}
            </h2>
            <p className={`text-sm mt-1 ${ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {!cert ? "Cette référence n'existe pas dans nos registres"
                : cert.revoked ? `Révoquée le ${formatDate(cert.revoked_at)}`
                : `Émise le ${formatDate(cert.issued_at)}`}
            </p>
            {cert?.revoked && cert.revoked_reason && (
              <p className="text-xs text-red-500 mt-2 italic px-4">
                Motif : « {cert.revoked_reason} »
              </p>
            )}
          </div>

          {/* Details */}
          {cert && (
            <div className="p-6 space-y-1">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Détails du document</p>
              <Row icon={User} label="Locataire" value={snap.tenant_name} />
              <Row icon={Phone} label="Téléphone" value={snap.tenant_phone} />
              <Row icon={Home} label="Adresse" value={[snap.property_address, snap.property_neighborhood, snap.property_city].filter(Boolean).join(', ')} />
              <Row icon={Building2} label="Agence émettrice" value={snap.agency_name} />
              <Row icon={Calendar} label="Date d'émission" value={formatDate(cert.issued_at)} />
              <Row icon={Shield} label="Émise par" value={`${cert.issued_by_name || '—'} (${cert.issued_by_role})`} />
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 p-4 text-center text-xs text-gray-500 border-t">
            Cette page est une vérification publique et n'expose que les informations strictement nécessaires.
            <br />
            Toute falsification expose son auteur à des sanctions pénales.
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyAttestation;
